import { type ReactNode } from 'react';

import { type Flow, type HandoverSpec } from '@/designs-dev/handover/types';
import mainPrs from '@/designs-dev/handover/main-prs.json';

const MERGED = new Set<number>(mainPrs.merged);

// One flows page for a piece of work, however many versions it has been through.
//
// The page opens on the newest version, and the older ones are a switch away
// rather than rows of their own in the menu. Version numbers follow the work:
// the big number moves on a major change (tokens, components, the shape of the
// flow), the kind engineering needs to look at closely; the small number is a
// round of changes on top of that major.
//
// A minor version is not a copy of its major. It is the major's spec with a few
// sections swapped, added or taken out, drawn under a `wrap` that changes what
// the screens say (see FlowVersionLayer). So 2.1 costs only what 2.1 changed,
// and 2.0 stays exactly as it was for comparing against.

export type SpecVersion = {
  /** '2.0', '2.1'. */
  n: string;
  /** What this version is, in a line. */
  note: string;
  /** The whole page for this version, not just what changed. */
  spec: HandoverSpec;
  /**
   * What the screens are drawn under: for a minor, the provider that makes its
   * major's screens say the minor's words.
   */
  wrap?: (page: ReactNode) => ReactNode;
  /** What is different from the version before, one line each. */
  changes?: string[];
  /**
   * Still in progress: the next version, not yet signed off. Named "4.2 draft"
   * wherever the version is named, so nobody builds from it by mistake. Drop
   * the flag at sign-off; the number stays.
   */
  draft?: boolean;
  /**
   * The Linear issue engineering builds this version from, e.g. 'ENG-3893'.
   * `/loft` checks it on boot: once it and its sub-issues are done, the
   * version is ready to become the page's `built` marker.
   */
  ticket?: string;
};

/** A version's name as people read it: "Version 4.1", or "Version 4.2 draft". */
export const versionName = (v: Pick<SpecVersion, 'n' | 'draft'>) =>
  `Version ${v.n}${v.draft ? ' draft' : ''}`;

export type VersionedSpec = {
  /** The page's id and route, the same for every version. */
  id: string;
  title: string;
  /** Oldest first. The last one is what the page opens on. */
  versions: SpecVersion[];
  /**
   * Versions from before this page existed, each still on a page of its own.
   * Listed in the history and linked to, oldest first. Not layered: they were
   * built as whole pages, and rebuilding history as layers is not worth it.
   */
  earlier?: { n: string; url: string; note: string; changes?: string[] }[];
  /**
   * The newest version that's on main, and the PR that put it there. Versions
   * build on each other, so engineering always builds the newest: everything up
   * to this one is built, everything after it isn't yet. A built version is
   * locked; a change to it is the next draft.
   *
   * The PR is the evidence. The page only says On main once that PR is in
   * main-prs.json (scripts/loft-main-prs.mjs), so a marker set ahead of the
   * merge, or a typo, reads as not built rather than as a false On main.
   */
  built?: { n: string; pr: number };
};

/**
 * Whether a version is on main. `n` can be any version on the page, current or
 * earlier. Without a marker, or before its PR has merged, nothing is.
 */
export const isBuilt = (spec: VersionedSpec, n: string): boolean => {
  const built = spec.built;
  if (!built || !MERGED.has(built.pr)) return false;
  const order = [
    ...(spec.earlier ?? []).map((v) => v.n),
    ...spec.versions.map((v) => v.n),
  ];
  const at = order.indexOf(built.n);
  const i = order.indexOf(n);
  return at !== -1 && i !== -1 && i <= at;
};

/** The marker's PR, when it's set but hasn't reached main yet. */
export const builtPending = (spec: VersionedSpec): number | undefined =>
  spec.built && !MERGED.has(spec.built.pr) ? spec.built.pr : undefined;

/**
 * A section brought into a version without the marks that said it was new: the
 * heading over a run of changes, the panel colour, the highlighted steps and the
 * red titles. On a page with a history those are the changelog's job, and a mark
 * left on the page would still be shouting "new" three versions later.
 */
const unmarked = (flow: Flow): Flow => {
  const {
    heading: _heading,
    background: _background,
    isNew: _isNew,
    ...rest
  } = flow;
  return {
    ...rest,
    steps: flow.steps.map(({ highlight: _h, isNew: _n, alt, ...step }) => ({
      ...step,
      ...(alt
        ? {
            alt: (Array.isArray(alt) ? alt : [alt]).map(
              ({ isNew: _a, ...a }) => a,
            ),
          }
        : {}),
    })),
  };
};

/** A section, by its title. */
type FlowEdit =
  | { replace: string; with: Flow }
  | { remove: string }
  | { insertAfter: string; flows: Flow[] };

/**
 * A spec with sections changed, by title. Edits apply in order; one naming a
 * section that is not there throws, so a renamed section in the major is caught
 * on the page instead of silently leaving the minor out of date.
 */
export const layer = (
  base: HandoverSpec,
  edits: FlowEdit[],
  over: Partial<HandoverSpec> = {},
): HandoverSpec => {
  let flows = [...base.flows];
  // What this layer changed, for the page's Highlight changes switch. The marks
  // come off the page, but what they pointed at is kept here.
  const changed = { flows: [] as string[], steps: [] as string[] };
  const find = (title: string) => {
    const i = flows.findIndex((f) => f.title === title);
    if (i < 0) throw new Error(`No section called "${title}" in ${base.id}`);
    return i;
  };
  for (const edit of edits) {
    if ('replace' in edit) {
      flows[find(edit.replace)] = unmarked(edit.with);
      // A replaced section that marked the steps it changed has those steps
      // highlighted; one that marked nothing changed as a whole.
      const marked = edit.with.steps
        .filter((s) => s.highlight || s.isNew)
        .map((s) => s.id);
      if (marked.length) changed.steps.push(...marked);
      else changed.flows.push(edit.with.title);
    } else if ('remove' in edit) {
      const i = find(edit.remove);
      flows = [...flows.slice(0, i), ...flows.slice(i + 1)];
    } else {
      const i = find(edit.insertAfter);
      flows = [
        ...flows.slice(0, i + 1),
        ...edit.flows.map(unmarked),
        ...flows.slice(i + 1),
      ];
      changed.flows.push(...edit.flows.map((f) => f.title));
    }
  }
  return { ...base, ...over, flows, changed };
};

/** The colour a highlighted change is drawn on, the one 2.1 marked its own in. */
const CHANGED = 'custom.gardeniaDark';

/** A spec with what it changed drawn on: whole sections, and single steps. */
export const highlighted = (spec: HandoverSpec): HandoverSpec => {
  const { changed } = spec;
  if (!changed) return spec;
  return {
    ...spec,
    flows: spec.flows.map((f) =>
      changed.flows.includes(f.title)
        ? { ...f, background: CHANGED }
        : {
            ...f,
            steps: f.steps.map((s) =>
              changed.steps.includes(s.id) ? { ...s, highlight: CHANGED } : s,
            ),
          },
    ),
  };
};

/** Whether a version has anything to highlight. */
export const hasChanges = (spec: HandoverSpec): boolean =>
  !!spec.changed && spec.changed.flows.length + spec.changed.steps.length > 0;
